import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  IconButton,
  Button,
  Box,
  InputAdornment,
  Divider,
  CircularProgress,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { useTheme } from '@mui/material/styles'
import toast from 'react-hot-toast'
import { InstructionsTabs } from './InstructionsTabs'

const pixelScript = (domain) =>
  `<script id="vtag-ai-js" async src="https://r2.leadsy.ai/tag.js" data-pid="${domain}" data-version="062024"></script>`

const SetupDialog = ({ open, onClose }) => {
  const theme = useTheme()
  const [domain, setDomain] = useState('')
  const [loading, setLoading] = useState(false)
  const [showResult, setShowResult] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)

  const handleGetPixel = () => {
    const isValidDomain = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/.test(domain.trim())

    if (!isValidDomain) {
      toast.error('Please enter a valid domain name (e.g. example.com)')
      return
    }

    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      setShowResult(true)
    }, 1500)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(pixelScript(domain))
    toast.success('Copied to clipboard')
  }

  const handleClose = () => {
    setShowResult(false)
    setDomain('')
    setShowInstructions(false)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: theme.palette.primary.primaryText
        }}
      >
        <Typography variant='h5' fontWeight='bold'>
          {showResult ? 'Installing script with HTML code' : 'Get Your Website Visitor Code'}
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon sx={{ color: theme.palette.text.secondary }} />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: theme.palette.divider }} />

      <DialogContent sx={{ pt: 3 }}>
        {!showResult
          ? (
            <>
              <Typography variant='body1' sx={{ mb: 2, color: theme.palette.text.primary }}>
                Welcome to Success.ai Website Visitor ID Tool!
              </Typography>

              <Typography variant='body2' sx={{ mb: 1, color: theme.palette.text.secondary }}>
                By setting up the website visitor pixel on your website, you'll be able to:
              </Typography>

              <List dense sx={{ mb: 2 }}>
                {[
                  'Identify and contact the people visiting your website.',
                  'Filter your website visitors to create targeted outreach campaigns.'
                ].map((item, i) => (
                  <ListItem key={i}>
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <FiberManualRecordIcon sx={{ fontSize: 8 }} />
                    </ListItemIcon>
                    <ListItemText primary={item} sx={{ color: theme.palette.text.secondary }} />
                  </ListItem>
                ))}
              </List>

              <TextField
                fullWidth
                placeholder='yourdomain.com'
                variant='outlined'
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ minWidth: '60px' }}
                      >
                        http(s)://
                      </Typography>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
              />

              <Box textAlign='center'>
                <Button
                  variant='contained'
                  onClick={handleGetPixel}
                  disabled={!domain || loading}
                  sx={{
                    px: 4,
                    py: 1.2,
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark
                    }
                  }}
                >
                  {loading && <CircularProgress size={18} sx={{ mr: 1, color: '#fff' }} />}
                  GET THE PIXEL
                </Button>
              </Box>
            </>
            )
          : (
            <>
              <Typography variant='body1' fontWeight='bold' mb={2}>Step 1:</Typography>
              <Box
                sx={{
                  bgcolor: '#1e1e1e',
                  color: '#fff',
                  p: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  position: 'relative',
                  overflowX: 'auto'
                }}
              >
                {pixelScript(domain)}
                <IconButton
                  onClick={handleCopy}
                  size='small'
                  sx={{ position: 'absolute', top: 4, right: 4, color: '#fff' }}
                >
                  <ContentCopyIcon fontSize='small' />
                </IconButton>
              </Box>

              <Typography variant='body1' fontWeight='bold' mt={3}>
                Step 2:
              </Typography>
              <Typography variant='body2' sx={{ mb: 1, color: theme.palette.text.secondary }}>
                Place it within the <code>&lt;head&gt;</code> tag of your website. (Your IT guy knows how 😄)
              </Typography>

              <Button
                size='small'
                onClick={() => setShowInstructions(prev => !prev)}
                sx={{
                  mb: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                Here’s how to do it
              </Button>

              <Collapse in={showInstructions}>
                <InstructionsTabs />
              </Collapse>

              <Typography variant='body1' fontWeight='bold' mt={3}>
                Step 3:
              </Typography>
              <Typography variant='body2' mb={1} color='text.secondary'>
                Test that your script is working.
              </Typography>

              <Button
                variant='outlined'
                sx={{
                  py: 1,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                Test script
              </Button>
            </>
            )}
      </DialogContent>
    </Dialog>
  )
}

export default SetupDialog
